import { useState, useCallback, useEffect, useRef } from 'react';

export interface CalculatorTab {
  id: string;
  name: string;
  data: Record<string, unknown>;
}

interface TabsState {
  tabs: CalculatorTab[];
  activeTabId: string;
  tabCounter: number;
}

const getStorageKey = (calculatorType: string) => `calculatorsheet_tabs_${calculatorType}`;

// Load tabs from localStorage
const loadTabs = (calculatorType: string, defaultData: Record<string, unknown>): TabsState => {
  try {
    const stored = localStorage.getItem(getStorageKey(calculatorType));
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed.tabs && parsed.tabs.length > 0) {
        return parsed;
      }
    }
  } catch (e) {
    console.error('Failed to load tabs:', e);
  }
  
  // Return default state
  return {
    tabs: [{ id: 'tab-1', name: 'Calculation 1', data: defaultData }],
    activeTabId: 'tab-1',
    tabCounter: 1,
  };
};

// Save tabs to localStorage
const saveTabs = (calculatorType: string, state: TabsState) => {
  try {
    localStorage.setItem(getStorageKey(calculatorType), JSON.stringify(state));
  } catch (e) {
    console.error('Failed to save tabs:', e);
  }
};

export function useCalculatorTabs(calculatorType: string, defaultData: Record<string, unknown>) {
  // Use a ref to track the default data to avoid stale closures
  const defaultDataRef = useRef(defaultData);
  defaultDataRef.current = defaultData;

  // Initialize state from localStorage
  const [state, setState] = useState<TabsState>(() => 
    loadTabs(calculatorType, defaultData)
  );

  const { tabs, activeTabId, tabCounter } = state;

  // Persist tabs whenever state changes
  useEffect(() => {
    saveTabs(calculatorType, state);
  }, [calculatorType, state]);

  const addTab = useCallback(() => {
    const newId = `tab-${Date.now()}`;
    setState(prev => {
      const newNumber = prev.tabCounter + 1;
      return {
        ...prev,
        tabCounter: newNumber,
        tabs: [...prev.tabs, { 
          id: newId, 
          name: `Calculation ${newNumber}`, 
          data: { ...defaultDataRef.current }
        }],
        activeTabId: newId,
      };
    });
  }, []);

  const removeTab = useCallback((id: string) => {
    setState(prev => {
      if (prev.tabs.length <= 1) return prev;
      
      const filteredTabs = prev.tabs.filter(t => t.id !== id);
      let newActiveId = prev.activeTabId;
      
      if (prev.activeTabId === id) {
        const idx = prev.tabs.findIndex(t => t.id === id);
        const newIdx = idx === 0 ? 1 : idx - 1;
        newActiveId = prev.tabs[newIdx]?.id || filteredTabs[0].id;
      }
      
      return {
        ...prev,
        tabs: filteredTabs,
        activeTabId: newActiveId,
      };
    });
  }, []);

  const duplicateTab = useCallback((id: string) => {
    setState(prev => {
      const tab = prev.tabs.find(t => t.id === id);
      if (!tab) return prev;
      
      const newId = `tab-${Date.now()}`;
      return {
        ...prev,
        tabs: [...prev.tabs, { 
          id: newId, 
          name: `${tab.name} (Copy)`, 
          // Deep clone the data to ensure complete isolation
          data: JSON.parse(JSON.stringify(tab.data))
        }],
        activeTabId: newId,
      };
    });
  }, []);

  const renameTab = useCallback((id: string, newName: string) => {
    setState(prev => ({
      ...prev,
      tabs: prev.tabs.map(t => 
        t.id === id ? { ...t, name: newName } : t
      ),
    }));
  }, []);

  const updateTabData = useCallback((id: string, data: Partial<Record<string, unknown>>) => {
    setState(prev => ({
      ...prev,
      tabs: prev.tabs.map(t => 
        t.id === id ? { ...t, data: { ...t.data, ...data } } : t
      ),
    }));
  }, []);

  const setActiveTabId = useCallback((id: string) => {
    setState(prev => ({
      ...prev,
      activeTabId: id,
    }));
  }, []);

  // Get active tab - ensure it always exists
  const activeTab = tabs.find(t => t.id === activeTabId) || tabs[0];

  return {
    tabs,
    activeTab,
    activeTabId,
    setActiveTabId,
    addTab,
    removeTab,
    duplicateTab,
    renameTab,
    updateTabData,
  };
}
